import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, updateDoc, doc, deleteDoc, orderBy, limit, serverTimestamp, startAfter, where, increment } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

interface Shot {
  id?: string;
  player: string;
  speed: number;
  timestamp: any;
}

const SHOTS_PER_PAGE = 5; // Number of shots to show per page

const ShotTracker: React.FC = () => {
  const [speed, setSpeed] = useState<string>('');
  const [player, setPlayer] = useState<string>('amustycow');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [lastSpeed, setLastSpeed] = useState<number | null>(null);
  const [recentShots, setRecentShots] = useState<Shot[]>([]);
  const [editingShot, setEditingShot] = useState<Shot | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [totalShots, setTotalShots] = useState(0);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const speedInputRef = useRef<HTMLInputElement>(null);

  // Fetch shots for current page
  const fetchShots = async (pageType: 'next' | 'prev' | 'first' = 'first') => {
    try {
      const shotsRef = collection(db, 'shots');
      let q;

      if (pageType === 'next' && lastVisible) {
        q = query(
          shotsRef,
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(SHOTS_PER_PAGE)
        );
      } else if (pageType === 'prev') {
        // For previous page, we need to fetch one more page backwards
        const backwardLimit = SHOTS_PER_PAGE * (currentPage - 1);
        q = query(
          shotsRef,
          orderBy('timestamp', 'desc'),
          limit(backwardLimit)
        );
      } else {
        // First page
        q = query(
          shotsRef,
          orderBy('timestamp', 'desc'),
          limit(SHOTS_PER_PAGE)
        );
      }

      const querySnapshot = await getDocs(q);
      
      // Get total count for pagination
      const totalQuery = await getDocs(collection(db, 'shots'));
      const totalShotsCount = totalQuery.size;
      setTotalShots(totalShotsCount);
      setTotalPages(Math.ceil(totalShotsCount / SHOTS_PER_PAGE));

      if (!querySnapshot.empty) {
        const shots = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Shot[];

        // If going backward, take the last SHOTS_PER_PAGE items
        const finalShots = pageType === 'prev' 
          ? shots.slice(-SHOTS_PER_PAGE)
          : shots;

        setRecentShots(finalShots);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setIsFirstPage(pageType === 'first' || currentPage === 1);
      } else {
        setRecentShots([]);
      }
    } catch (error) {
      console.error('Error fetching shots:', error);
    }
  };

  // Initial fetch and refetch when message changes
  useEffect(() => {
    fetchShots('first');
  }, [message]);

  // Add effect to focus input when speed changes to empty
  useEffect(() => {
    if (speed === '') {
      const focusTimer = setTimeout(() => {
        speedInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(focusTimer);
    }
  }, [speed]);

  const handlePageChange = async (direction: 'next' | 'prev') => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      await fetchShots('next');
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      await fetchShots('prev');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!speed) return;

    setLoading(true);
    try {
      const speedNum = parseInt(speed);
      const shotData = {
        player,
        speed: speedNum,
        timestamp: serverTimestamp(),
        recordedBy: currentUser?.email || 'hishima'
      };

      // Add new shot directly without checking for duplicates
      await addDoc(collection(db, 'shots'), shotData);

      setLastSpeed(speedNum);
      setSpeed('');
      setMessage(`Shot speed of ${speedNum} KPH recorded for ${player}`);
      fetchShots(); // Refresh the shots list
    } catch (error) {
      console.error('Error adding shot:', error);
      setMessage('Error recording shot. Please try again.');
    } finally {
      setLoading(false);
      speedInputRef.current?.focus();
    }
  };

  const handleEdit = (shot: Shot) => {
    setEditingShot(shot);
    setSpeed(shot.speed.toString());
    setPlayer(shot.player);
  };

  const handleDelete = async (shotId: string) => {
    if (window.confirm('Are you sure you want to delete this shot?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'shots', shotId));
        setMessage('Shot deleted successfully!');
        // Refresh the current page after deletion
        fetchShots(currentPage === 1 ? 'first' : 'prev');
      } catch (error) {
        console.error('Error deleting shot:', error);
        setMessage('Error deleting shot. Please try again.');
      } finally {
        setLoading(false);
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingShot || !speed || isNaN(Number(speed))) return;
    
    setLoading(true);
    const speedNum = Number(speed);

    try {
      await updateDoc(doc(db, 'shots', editingShot.id!), {
        speed: speedNum,
        player,
        timestamp: serverTimestamp()
      });
      setMessage('Shot updated successfully!');
      setEditingShot(null);
      setSpeed('');
      // Refresh the current page after update
      fetchShots(currentPage === 1 ? 'first' : 'prev');
    } catch (error) {
      console.error('Error updating shot:', error);
      setMessage('Error updating shot. Please try again.');
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleUndo = async () => {
    try {
      setLoading(true);
      const shotsRef = collection(db, 'shots');
      const q = query(shotsRef, orderBy('timestamp', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const lastShot = querySnapshot.docs[0];
        const shotData = lastShot.data() as Shot;
        
        if (window.confirm(`Are you sure you want to remove the last shot? (${shotData.player}: ${shotData.speed} KPH)`)) {
          await deleteDoc(doc(db, 'shots', lastShot.id));
          setMessage('Last shot removed!');
          speedInputRef.current?.focus();
          // Refresh the first page after undo
          fetchShots('first');
        }
      }
    } catch (error) {
      console.error('Error removing shot:', error);
      setMessage('Error removing shot. Please try again.');
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#f47521] mb-2">Shot Speed Tracker</h1>
        <div className="flex items-center space-x-4 text-gray-300">
          <p>Last Shot: {lastSpeed ? `${lastSpeed} KPH` : 'No shots recorded'}</p>
          <p>Total Shots: {totalShots}</p>
        </div>
      </div>
      <div className="max-w-md mx-auto bg-background-card rounded-lg shadow-lg">
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-primary">Shot Speed Tracker</h2>
            {lastSpeed !== null && (
              <div className="text-right">
                <p className="text-sm text-text-secondary">Last Entered Speed</p>
                <p className="text-xl font-bold text-primary">{lastSpeed} KPH</p>
              </div>
            )}
          </div>
          {message && (
            <div className="bg-background-navy border border-primary text-primary px-4 py-2 rounded">
              {message}
            </div>
          )}
          <form onSubmit={editingShot ? handleUpdate : handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Player</label>
              <select
                value={player}
                onChange={(e) => setPlayer(e.target.value)}
                className="w-full p-2 bg-background-navy border border-accent-purple rounded text-text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                disabled={loading || !!editingShot}
              >
                <option value="amustycow">amustycow</option>
                <option value="uuest">uuest</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Shot Speed (KPH)</label>
              <input
                ref={speedInputRef}
                type="number"
                value={speed}
                onChange={(e) => setSpeed(e.target.value)}
                className="w-full p-2 bg-background-navy border border-accent-purple rounded text-text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                placeholder="Enter speed in KPH"
                required
                disabled={loading}
              />
            </div>
            <div className="flex space-x-4 pt-2">
              <button
                type="submit"
                className="flex-1 bg-primary hover:bg-primary-hover text-text-primary font-bold py-2 px-4 rounded transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Processing...' : editingShot ? 'Update Shot' : 'Add Shot'}
              </button>
              {editingShot && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingShot(null);
                    setSpeed('');
                  }}
                  className="flex-1 bg-background-navy border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-text-primary font-bold py-2 px-4 rounded transition-colors"
                  disabled={loading}
                >
                  Cancel Edit
                </button>
              )}
              {!editingShot && (
                <button
                  type="button"
                  onClick={() => navigate('/leaderboard')}
                  className="flex-1 bg-background-navy border border-primary text-primary hover:bg-primary hover:text-text-primary font-bold py-2 px-4 rounded transition-colors"
                  disabled={loading}
                >
                  View Leaderboard
                </button>
              )}
            </div>
          </form>
          <button
            onClick={handleUndo}
            className="w-full mt-4 bg-background-navy border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-text-primary font-bold py-2 px-4 rounded transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Undo Last Shot'}
          </button>

          {/* Recent Shots History with Pagination */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-primary mb-4">Recent Shots</h3>
            <div className="space-y-2">
              {recentShots.map((shot) => (
                <div key={shot.id} className="flex items-center justify-between bg-background-navy p-3 rounded">
                  <div>
                    <span className="font-bold text-primary">{shot.player}</span>
                    <span className="text-text-secondary mx-2">•</span>
                    <span className="text-text-primary">{shot.speed} KPH</span>
                    <span className="text-text-secondary mx-2">•</span>
                    <span className="text-text-secondary">
                      {shot.timestamp?.toDate().toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(shot)}
                      className="text-blue-500 hover:text-blue-400 transition-colors"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(shot.id!)}
                      className="text-red-500 hover:text-red-400 transition-colors"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {recentShots.length === 0 && (
                <p className="text-text-secondary text-center py-4">No shots recorded yet</p>
              )}
            </div>

            {/* Pagination Controls */}
            {recentShots.length > 0 && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => handlePageChange('prev')}
                  disabled={isFirstPage}
                  className={`px-4 py-2 rounded ${
                    isFirstPage
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary-hover'
                  } text-text-primary transition-colors`}
                >
                  Previous
                </button>
                <span className="text-text-secondary">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange('next')}
                  disabled={currentPage >= totalPages}
                  className={`px-4 py-2 rounded ${
                    currentPage >= totalPages
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary-hover'
                  } text-text-primary transition-colors`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShotTracker; 